import { Fragment, ReactNode, useMemo } from 'react'

type Props = {
  keywords: string[]
  insensitiveJapanese?: boolean
  children: string
}

export const HighlightText = ({
  children,
  keywords,
  insensitiveJapanese,
}: Props) => {
  const render = useMemo(() => {
    if (!keywords?.length || !children) return

    const lowerText = children.toLowerCase()

    const highlightList: [number, number][] = keywords.flatMap((keyword) => {
      const lowerKeyword = keyword.toLowerCase()
      const result: [number, number][] = []

      for (let i = 0; i <= lowerText.length - lowerKeyword.length; i++) {
        // slice text to be same size as each keyword
        const subText = lowerText.slice(i, i + lowerKeyword.length)

        // check for same hiragana/katakana full/half with
        if (
          subText.localeCompare(lowerKeyword, insensitiveJapanese && 'ja') === 0
        ) {
          result.push([i, i + lowerKeyword.length - 1])
        }
      }

      return result
    })

    if (!highlightList.length) return

    const refinedHighlight: [number, number][] = []

    // sort and merge index pair if they connected
    highlightList.sort((a, b) => a[0] - b[0] || a[1] - b[1])
    highlightList.forEach((pair) => {
      const last = refinedHighlight[refinedHighlight.length - 1]
      if (!last || pair[0] > last[1]) refinedHighlight.push(pair)
      else last[1] = pair[1]
    })

    const result: ReactNode[] = []

    // --- normal text, *** highlight text
    // every text will be treat as it is pattern ---***---***---***---... and so on
    // some [*] or [-] part can be length of 0

    let last = -1
    refinedHighlight.forEach(([start, end], i) => {
      // start with [-]part before [*]part
      if (last + 1 < start) {
        result.push(
          <Fragment key={i * 2}>{children.slice(last + 1, start)}</Fragment>
        )
      }

      // add [*] part
      result.push(<b key={i * 2 + 1}>{children.slice(start, end + 1)}</b>)
      last = end
    })

    // add rest of [-]part
    if (last + 1 < children.length)
      result.push(
        <Fragment key="rest">{children.substring(last + 1)}</Fragment>
      )

    return result
  }, [children, keywords])

  return <Fragment>{render ?? children}</Fragment>
}
